import { useMemo, useCallback } from 'react'
import useBoardingVisit from './useBoardingVisit'
import useDaycareVisit from './useDaycareVisit'
import useSelectedHousehold from './useSelectedHousehold'
import { getDaysBetweenDates, formatNumsToDollars, VISIT_TYPES } from '../utils'

const CLIENT_ALREADY_HAS_BOARDING_VISIT = 'Client has a boarding visit on this day already.'
const CLIENT_HAS_BOARDING_AND_DAYCARE_VISIT = 'Client has a boarding and daycare visit on this day already.'

const useDayRestrictions = () => {
	const { householdVisits } = useSelectedHousehold()
	const { daycareHolidays } = useDaycareVisit()
	const { boardingHolidays } = useBoardingVisit()

	/**
	 * Map of dates to restrictions
	 * @type {Map<'YYYY-MM-DD', {
	 * boarding: [{type: string, name: string, isDropOffPickupRestricted: boolean, surchargeFee: number, isOvercap: boolean, daycareAllowed: boolean, tooltip: string}],
	 * daycare: [{type: string, name: string,auto: boolean, isDropOffPickupRestricted: boolean, isDaycareHoliday: boolean, surchargeFee: number, daycareAllowed: boolean, tooltip: string}],
	 * household: [{type: string, dates: ['YYYY-MM-DD'], pet_ids: string[], tooltip: string}],
	 * daycareAllowed: boolean
	 * }>}
	 */
	const dayRestrictionsMap = useMemo(() => {
		const map = new Map()

		// boarding holidays are filtered in the hook and include holidays from 1 month before and after
		for (const holiday of boardingHolidays) {
			const {
				startDate,
				endDate,
				surcharge_dates = [],
				surcharge_fee,
				restricted_pick_drop_dates = [],
				over_cap = false,
				type,
				name,
				deposit_required
			} = holiday
			const days = getDaysBetweenDates(startDate, endDate)

			for (const day of days) {
				const daycare_not_allowed = restricted_pick_drop_dates.includes(day)
				const reducedBoardingHoliday = {
					type,
					name,
					isDropOffPickupRestricted: false,
					surchargeFee: 0,
					isOvercap: over_cap,
					daycareAllowed: !daycare_not_allowed,
					tooltip: '',
					deposit_required
				}

				const hasSurcharge = surcharge_dates.includes(day)
				const isDropOffPickupRestricted = restricted_pick_drop_dates.includes(day)
				if (hasSurcharge) {
					reducedBoardingHoliday.surchargeFee = surcharge_fee
					reducedBoardingHoliday.tooltip = `+${formatNumsToDollars(surcharge_fee)} for ${name || 'holiday'
						}.`
				}
				if (isDropOffPickupRestricted) {
					reducedBoardingHoliday.isDropOffPickupRestricted = true
					reducedBoardingHoliday.tooltip = 'Drop-off and pick-up restricted on this day.'
				}
				if (daycare_not_allowed) {
					reducedBoardingHoliday.tooltip += ' Daycare not available.'
				}
				if (deposit_required) {
					reducedBoardingHoliday.tooltip += ' Deposit required.'
				}
				if (!map.has(day)) {
					map.set(day, { boarding: [reducedBoardingHoliday], daycareAllowed: !daycare_not_allowed })
				} else {
					const current = map.get(day)
					map.set(day, { ...current, boarding: [...current.boarding, reducedBoardingHoliday] })
				}
			}
		}

		// Existing household visits
		for (const householdVisit of householdVisits) {
			const {
				dates,
				type,
				pet_ids,
				early_dropoff,
				extended_pickup,
				first_day_daycare,
				last_day_daycare,
				status,
			} = householdVisit

			if (type === 'training_class') {
				continue
			}

			const first_day = dates[0]
			const last_day = dates[dates.length - 1]

			// @TODO UPDATE THIS LOGIC TO INCLUDE DAYCARE AND GROOMING VISITS.
			// WAS PORTED OVER FROM THE BOARDING WIZARD SO ONLY HAS BOARDING LOGIC CURRENTLY!!
			if (type !== VISIT_TYPES.Boarding) {
				continue
			}

			for (const day of dates) {
				const reducedHouseholdVisit = {
					type,
					dates,
					pet_ids,
					status,
					tooltip: CLIENT_ALREADY_HAS_BOARDING_VISIT
				}

				if (day === first_day) {
					reducedHouseholdVisit['first_day_of_visit'] = true
					if (first_day_daycare === true) {
						reducedHouseholdVisit.tooltip = CLIENT_HAS_BOARDING_AND_DAYCARE_VISIT
					}
					if (extended_pickup === true) {
						reducedHouseholdVisit['early_dropoff'] = early_dropoff
					}
				}

				if (day === last_day) {
					reducedHouseholdVisit['last_day_of_visit'] = true
					if (last_day_daycare === true) {
						reducedHouseholdVisit.tooltip = CLIENT_HAS_BOARDING_AND_DAYCARE_VISIT
					}
					if (extended_pickup === true) {
						reducedHouseholdVisit['extended_pickup'] = extended_pickup
					}
				}

				if (!map.has(day)) {
					map.set(day, { household: [reducedHouseholdVisit] })
				} else {
					const current = map.get(day)
					if (!current.household) {
						map.set(day, { ...current, household: [reducedHouseholdVisit] })
					} else {
						map.set(day, {
							...current,
							household: [...current.household, reducedHouseholdVisit]
						})
					}
				}
			}
		}

		// daycare holidays are filtered in the hook and include holidays from 1 month before and after
		for (const holiday of daycareHolidays) {
			const {
				type,
				startDate,
				endDate,
				auto = false,
				name,
				surcharge_dates = [],
				surcharge_fee = 0,
				restricted_pick_drop_dates = [],
				overflow_dogs
			} = holiday
			const days = getDaysBetweenDates(startDate, endDate)



			for (const day of days) {
				const daycare_allowed = !restricted_pick_drop_dates.includes(day)

				const reducedDaycareHoliday = {
					type,
					name,
					auto,
					isDropOffPickupRestricted: false,
					isDaycareHoliday: true,
					surchargeFee: 0,
					daycareAllowed: daycare_allowed,
					tooltip: ''
				}

				const hasSurcharge = surcharge_dates.includes(day)
				const isDropOffPickupRestricted = restricted_pick_drop_dates.includes(day)
				if (hasSurcharge) {
					reducedDaycareHoliday.surchargeFee = surcharge_fee
					reducedDaycareHoliday.tooltip = `+${formatNumsToDollars(surcharge_fee)} for holiday`
				}
				if (isDropOffPickupRestricted) {
					reducedDaycareHoliday.isDropOffPickupRestricted = true
				}
				reducedDaycareHoliday.isDaycareHoliday = true

				if (overflow_dogs) {
					reducedDaycareHoliday['overflow_dogs'] = overflow_dogs
				}

				if (!map.has(day)) {
					map.set(day, { daycare: [reducedDaycareHoliday], daycareAllowed: daycare_allowed })
				} else {
					const current = map.get(day)
					if (!current.daycare) {
						map.set(day, {
							...current,
							daycare: [reducedDaycareHoliday],
							daycareAllowed: daycare_allowed
						})
					} else {
						map.set(day, { ...current, daycare: [...current.daycare, reducedDaycareHoliday] })
					}
				}
			}
		}

		// console.log('dayRestrictionsMap', map)

		return map
	}, [daycareHolidays, boardingHolidays, householdVisits])

	/**
	 * Get restrictions for a given date
	 * @param {string} params.date - YYYY-MM-DD
	 * @param {string} params.type 'boarding' | 'daycare' | 'household' | 'daycareAllowed'
	 * @returns {object | null} restrictions
	 */
	const getDateRestrictions = useCallback(
		({ date, type = null }) => {
			if (!date) {
				return null
			}

			const restrictions = dayRestrictionsMap.get(date)
			if (!restrictions) {
				return null
			}

			if (type) {
				return restrictions[type] || null
			}

			return restrictions
		},
		[dayRestrictionsMap]
	)

	return { dayRestrictionsMap, getDateRestrictions }
}

export default useDayRestrictions
